// Opportunity Explorer v2 — verdict-first region cards, per mockup
// screenA-explorer-v2.html. Story #5305 §5.1 (Task #5325), superseding the
// v1 band-range-label card from Story #4937 Stream 2b.
//
// Cards lead with the server-templated verdict sentence, name their band on
// the same distance ladder every surface shares, and disclose the four raw
// sub-scores behind a native <details> (no per-card React state needed).
// Adjacent all-empty bands collapse into one card with an all-or-rollback
// watch fan-out — src/lib/whitespace.ts's groupRegionsForExplorer() and
// planCollapsedWatchToggle() do the grouping/planning math; this component
// only renders the plan and reports the click.
//
// Pure presentational component: all scoring/sorting/labelling/grouping
// math lives in src/lib/whitespace.ts so it stays coverage-testable without
// canvas/DOM.
import { Compass, ShieldCheck, Sparkles } from 'lucide-react'
import HelpPopover from '../help/HelpPopover'
import {
  bandPrintedRange,
  bandSimilarityAnchor,
  BAND_CARD_CAPTION,
  collapsedGroupCaption,
  collapsedGroupHeaderName,
  collapsedGroupPrintedRange,
  collapsedGroupVerdict,
  collapsedWatchLabel,
  collapsedWatchState,
  EXPLORER_RANK_STRIP_TEXT,
  formatWhitespaceScore,
  groupRegionsForExplorer,
  regionBandKey,
  regionBandName,
  regionChips,
  regionVerdict,
  scoreRingGradient,
  subScoreExplanation,
} from '../../lib/whitespace'
import { BandGlyph } from './BandGlyph'
import type { WhitespaceRegion } from '../../types/whitespace'

const SUB_SCORE_BARS: { key: 'sparsity' | 'adjacency' | 'momentum' | 'enablement'; label: string; color: string }[] = [
  { key: 'sparsity', label: 'Openness', color: '#FBBF24' },
  { key: 'adjacency', label: 'Nearby activity', color: '#0EA5E9' },
  { key: 'momentum', label: 'Filing momentum', color: '#8B5CF6' },
  { key: 'enablement', label: 'Feasibility signal', color: '#22C55E' },
]

interface OpportunityExplorerProps {
  regions: WhitespaceRegion[]
  onOpenDetail: (region: WhitespaceRegion) => void
  onRunNoveltyProbe: (region: WhitespaceRegion) => void
  onToggleWatch: (region: WhitespaceRegion) => void
  /** Opens the multi-vendor Analysis Panel (Story #5116 Stream 9e) for this
   * card's already-resolved boundary record ids
   * (patent_record_ids/academic_record_ids). */
  onRunAnalysisPanel: (region: WhitespaceRegion) => void
  /** Fires the all-or-rollback fan-out watch/unwatch for a collapsed card's
   * whole member set (design §5.1). Absent group handling falls back to
   * the single-region path so a lone card never needs this prop wired. */
  onToggleWatchGroup: (regions: WhitespaceRegion[]) => void
  /** Band keys (regionBandKey) of regions the caller already watches — real
   * state from GET /whitespace/watch, Story #4937 Stream 4d. Empty set until
   * the watch list has loaded. */
  watchedBandKeys?: Set<string>
}

export default function OpportunityExplorer({
  regions,
  onOpenDetail,
  onRunNoveltyProbe,
  onToggleWatch,
  onRunAnalysisPanel,
  onToggleWatchGroup,
  watchedBandKeys = new Set(),
}: OpportunityExplorerProps) {
  const watchMap: Record<string, string> = {}
  for (const key of watchedBandKeys) watchMap[key] = key
  const cards = groupRegionsForExplorer(regions)

  return (
    <div data-testid="opportunity-explorer">
      {cards.length > 0 && (
        <div
          className="flex items-center gap-1.5 bg-sky-50 border border-sky-200/70 rounded-lg px-3 py-2 mb-3 text-[12px] text-iflow-900"
          data-testid="explorer-rank-strip"
          data-tour="ws-rank-strip"
        >
          ⚖️ {EXPLORER_RANK_STRIP_TEXT}
          <HelpPopover topic="ws-why-ranked" size={13} label="Help: why cards are ranked this way" />
          <span className="text-gray-300">·</span>
          <HelpPopover topic="ws-bands" size={13} label="Help: the distance bands" />
          <span className="text-gray-300">·</span>
          <HelpPopover topic="ws-how-scored" size={13} label="Help: how the score is built" />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cards.map((card) => {
          if (card.regions.length === 1) {
            return (
              <RegionCard
                key={regionBandKey(card.regions[0].band)}
                region={card.regions[0]}
                isWatched={watchedBandKeys.has(regionBandKey(card.regions[0].band))}
                onOpenDetail={onOpenDetail}
                onRunNoveltyProbe={onRunNoveltyProbe}
                onToggleWatch={onToggleWatch}
                onRunAnalysisPanel={onRunAnalysisPanel}
              />
            )
          }
          return (
            <CollapsedCard
              key={card.regions.map((r) => regionBandKey(r.band)).join('|')}
              regions={card.regions}
              watchMap={watchMap}
              onOpenDetail={onOpenDetail}
              onToggleWatchGroup={onToggleWatchGroup}
            />
          )
        })}

        {/* Probe card — always present, per mockup, so a user can score their own concept */}
        <div className="light-card p-4 flex gap-4 border-dashed border-2 bg-white/40" data-testid="opportunity-probe-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
            <Compass className="w-6 h-6 text-gray-400" />
          </div>
          <div className="flex-1">
            <div className="text-[14.5px] font-extrabold text-gray-600 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Test your own adjacent concept
            </div>
            <p className="text-[11.8px] text-gray-600 leading-snug mt-1">
              Describe a variation in a sentence — it is embedded with the same model, placed on the map, and scored
              against the corpus like any generated candidate.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ScoreRing({ score }: { score: number }) {
  return (
    <div
      className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 relative"
      style={{ background: scoreRingGradient(score) }}
    >
      <div className="w-[50px] h-[50px] rounded-full bg-white flex items-center justify-center">
        <b className="text-lg text-iflow-900">{formatWhitespaceScore(score)}</b>
      </div>
      <small className="absolute -bottom-4 w-full text-center text-[9px] font-bold text-gray-400 tracking-wide">
        SCORE
      </small>
    </div>
  )
}

interface RegionCardProps {
  region: WhitespaceRegion
  isWatched: boolean
  onOpenDetail: (region: WhitespaceRegion) => void
  onRunNoveltyProbe: (region: WhitespaceRegion) => void
  onToggleWatch: (region: WhitespaceRegion) => void
  onRunAnalysisPanel: (region: WhitespaceRegion) => void
}

function RegionCard({ region, isWatched, onOpenDetail, onRunNoveltyProbe, onToggleWatch, onRunAnalysisPanel }: RegionCardProps) {
  const range = bandPrintedRange(region.band)
  return (
    <div className="light-card p-4 flex gap-4" data-testid="opportunity-card">
      <ScoreRing score={region.wos_score} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-iflow-700 uppercase tracking-wide">
          <BandGlyph bandKey={regionBandKey(region.band)} />
          <span>{regionBandName(region)} · {bandSimilarityAnchor(range)}</span>
        </div>
        <div className="text-[10.5px] text-gray-400">{BAND_CARD_CAPTION}</div>
        <div className="text-[14.5px] font-extrabold text-iflow-900 mt-1.5">{regionVerdict(region)}</div>
        <p className="text-[12px] text-gray-700 mt-1">
          {region.patent_count} patent{region.patent_count === 1 ? '' : 's'} · {region.academic_count} paper
          {region.academic_count === 1 ? '' : 's'}
        </p>

        {regionChips(region).length > 0 && (
          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {regionChips(region).map((chip) => (
              <span key={chip} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-sky-500/10 text-iflow-700">
                {chip}
              </span>
            ))}
          </div>
        )}

        <details className="mt-2.5 border-t border-dashed border-sky-200 pt-2">
          <summary className="text-[11.5px] font-bold text-iflow-700 cursor-pointer select-none">
            Why this score?
          </summary>
          <div className="mt-1.5 space-y-1.5">
            {SUB_SCORE_BARS.map(({ key, label, color }) => {
              const value = region[key]
              return (
                <div key={key} className="grid grid-cols-[110px_1fr] gap-2 items-center text-[10.5px] text-gray-500">
                  <span className="font-bold text-iflow-900">{label}</span>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
                    </div>
                    <span className="text-gray-500 whitespace-nowrap">
                      {formatWhitespaceScore(value)}/100 — {subScoreExplanation(key, region)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </details>

        <div className="flex gap-1.5 mt-2.5 flex-wrap">
          <button
            onClick={() => onOpenDetail(region)}
            className="px-2.5 py-1 text-[11px] font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors"
          >
            Open detail
          </button>
          <button
            onClick={() => onRunNoveltyProbe(region)}
            className="px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors"
          >
            Run novelty probe
          </button>
          <button
            onClick={() => onRunAnalysisPanel(region)}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors"
          >
            <ShieldCheck className="w-3 h-3" />
            Run analysis panel · uses AI credits
          </button>
          <button
            onClick={() => onToggleWatch(region)}
            aria-pressed={isWatched}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
              isWatched
                ? 'bg-sky-500/10 border border-sky-300 text-iflow-700'
                : 'border border-sky-200 text-gray-700 hover:bg-sky-50'
            }`}
          >
            {isWatched ? '★ Watching' : '☆ Watch'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface CollapsedCardProps {
  regions: WhitespaceRegion[]
  watchMap: Record<string, string>
  onOpenDetail: (region: WhitespaceRegion) => void
  onToggleWatchGroup: (regions: WhitespaceRegion[]) => void
}

function CollapsedCard({ regions, watchMap, onOpenDetail, onToggleWatchGroup }: CollapsedCardProps) {
  const range = collapsedGroupPrintedRange(regions)
  const watchState = collapsedWatchState(regions, watchMap)
  // Every member has the identical wos_score (an all-empty region's score
  // never depends on which band it's in — see subScoreExplanation's
  // module-note on momentum) — using the first is exact, not an average.
  const score = regions[0].wos_score

  return (
    <div className="light-card p-4 flex gap-4 bg-gradient-to-b from-white to-sky-50/40" data-testid="opportunity-card-collapsed">
      <ScoreRing score={score} />

      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">
          {collapsedGroupHeaderName(regions)} · similarity {range[0]}–{range[1]}
        </div>
        <div className="text-[10.5px] text-gray-400">{collapsedGroupCaption(regions.length)}</div>
        <div className="text-[14.5px] font-extrabold text-iflow-900 mt-1.5">Nothing recorded here at all</div>
        <p className="text-[12px] text-gray-700 mt-1">
          0 patents · 0 papers ·{' '}
          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">Fully white</span>
        </p>
        <p className="text-[11.5px] text-gray-500 mt-2 leading-snug">{collapsedGroupVerdict(regions)}</p>

        <div className="flex gap-1.5 mt-2.5 flex-wrap">
          <button
            onClick={() => onOpenDetail(regions[0])}
            className="px-2.5 py-1 text-[11px] font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors"
          >
            Open detail
          </button>
          <button
            onClick={() => onToggleWatchGroup(regions)}
            aria-pressed={watchState.isFullyWatched}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
              watchState.isFullyWatched
                ? 'bg-sky-500/10 border border-sky-300 text-iflow-700'
                : 'border border-sky-200 text-gray-700 hover:bg-sky-50'
            }`}
          >
            {collapsedWatchLabel(watchState)}
          </button>
        </div>
      </div>
    </div>
  )
}
